import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, subDays } from 'date-fns';
import { BarChart3 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { fetchProgramInterestGaps } from '../api';
import { GRAPH_REFRESH_INTERVAL_MS } from '../config/constants';
import useTimeZone from '../hooks/useTimeZone';
import { formatDayMonthYear } from '../utils/dateTime';

const defaultRange = {
  from: subDays(new Date(), 29),
  to: new Date()
};

const formatPercent = (value) =>
  typeof value === 'number' ? `${value.toFixed(1)}%` : `${Number(value || 0).toFixed(1)}%`;

const EmptyState = ({ text }) => (
  <div className="flex h-56 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
    {text}
  </div>
);

const MesorealDashboardWidgets = ({ dateRange, callType = 'all' }) => {
  const [programGaps, setProgramGaps] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const isFetching = useRef(false);
  const { timeZone } = useTimeZone();

  const activeDateRange = useMemo(() => {
    if (dateRange?.from && dateRange?.to) {
      return dateRange;
    }
    return defaultRange;
  }, [dateRange]);

  const dateRangeLabel = useMemo(() => {
    if (!activeDateRange?.from || !activeDateRange?.to) {
      return 'за выбранный период';
    }

    const fromLabel = formatDayMonthYear(activeDateRange.from, timeZone);
    const toLabel = formatDayMonthYear(activeDateRange.to, timeZone);
    return `за период ${fromLabel} — ${toLabel}`;
  }, [activeDateRange, timeZone]);

  const load = useCallback(async (showSpinner = true) => {
    if (!activeDateRange?.from || !activeDateRange?.to || isFetching.current) {
      return;
    }

    if (showSpinner) {
      setIsLoading(true);
    }

    isFetching.current = true;
    try {
      const params = {
        date_from: format(activeDateRange.from, 'yyyy-MM-dd'),
        date_to: format(activeDateRange.to, 'yyyy-MM-dd'),
        call_type: callType === 'all' ? undefined : callType
      };
      const gapData = await fetchProgramInterestGaps(params);
      setProgramGaps(Array.isArray(gapData) ? gapData : []);
    } catch {
      setProgramGaps([]);
    } finally {
      isFetching.current = false;
      if (showSpinner) {
        setIsLoading(false);
      }
    }
  }, [activeDateRange, callType]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const intervalId = setInterval(() => load(false), GRAPH_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [load]);

  const gapData = useMemo(
    () =>
      (programGaps ?? []).map((row) => ({
        label: row.label ?? row.serviceArea,
        interestedCalls: row.interestedCalls ?? 0,
        notAchievedCalls: row.notAchievedCalls ?? 0,
        nextStepCalls: row.nextStepCalls ?? 0,
        refusedOrStalledCalls: row.refusedOrStalledCalls ?? 0,
        gapRate: Number(row.gapRate ?? 0)
      })).sort((left, right) =>
        (Number(right.gapRate) || 0) - (Number(left.gapRate) || 0)
        || (Number(right.notAchievedCalls) || 0) - (Number(left.notAchievedCalls) || 0)
        || (Number(right.interestedCalls) || 0) - (Number(left.interestedCalls) || 0)
      ),
    [programGaps]
  );

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader className="space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Интерес без записи по программам
          </CardTitle>
          <CardDescription>Программы учебного центра, где клиент интересовался, но запись или оплата не завершены {dateRangeLabel}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="mb-3 text-sm text-muted-foreground">Загрузка...</p>}
          {gapData.length === 0 ? (
            <EmptyState text="Нет программ с незавершенным целевым действием." />
          ) : (
            <div className="space-y-3">
              {gapData.map((row) => (
                <div key={row.label} className="rounded-md border bg-muted/20 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{row.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {row.notAchievedCalls.toLocaleString('ru-RU')} из {row.interestedCalls.toLocaleString('ru-RU')} звонков с интересом не дошли до записи или оплаты
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold">{formatPercent(row.gapRate)}</div>
                      <div className="text-xs text-muted-foreground">разрыв</div>
                    </div>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-gray-200 dark:bg-gray-800">
                    <div
                      className="h-2 rounded-full bg-orange-500"
                      style={{ width: `${Math.max(0, Math.min(100, row.gapRate))}%` }}
                    />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <span>Следующий шаг: {row.nextStepCalls.toLocaleString('ru-RU')}</span>
                    <span>Отказ/зависло: {row.refusedOrStalledCalls.toLocaleString('ru-RU')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MesorealDashboardWidgets;
